///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { bidScacType, mode as modeType } from "./types";

const processLocationCode = (locationCode) => {
  if (!locationCode) return null;
  return Array.isArray(locationCode) 
    ? locationCode.map(c => c.value) 
    : locationCode;
};

const processFoCodes = (bid, isCoyoteBid) => {
  if (isCoyoteBid) return null;

  if (bid.freightOptimizerCodes) {
    if (Array.isArray(bid.freightOptimizerCodes)) {
      return Array.from(new Set(bid.freightOptimizerCodes));
    }
    return null;
  }

  return null;
};

export const mapBidDetails = (bid, isPricedBySales, crmEnableImdlUi = false) => {
  const isCoyoteBid = bid.bidScac.value === bidScacType[0].value;
  const processedFoCodes = processFoCodes(bid, isCoyoteBid);

  const mappedBidDetails = {
    // General
    isCoyoteBid: isCoyoteBid,
    bidId: bid.bidId ? bid.bidId : 0,
    name: bid.rfqName,
    shipperId: isCoyoteBid ? bid.shipper.value : null,
    shipperCode: isCoyoteBid
      ? bid.shipper.code
      : bid.bidId
        ? bid.foShipperCode || null
        : null,
    shipperName: isCoyoteBid ? bid.shipper.name : null,
    mainRepId: isCoyoteBid ? bid.mainSalesRepDetails.mainSalesRep.userId : null,
    mainRepName: isCoyoteBid
      ? bid.mainSalesRepDetails.mainSalesRep.fullName
      : bid.accountOwner,
    outputFormats: bid.bidOutputFormat ? bid.bidOutputFormat.map(f => f.value) : [],
    contractOnFile: bid.contractOnFile ? bid.contractOnFile.value : null,
    mileageBasis: bid.mileageBasis ? bid.mileageBasis.value : null,
    tenderLeadTime: bid.tenderLeadTime ? bid.tenderLeadTime.value : null,
    shipperOpsGroup:
      isCoyoteBid && bid.shipperOpsGroup ? parseInt(bid.shipperOpsGroup) : null,
    shipperOpsGroupOffice:
      isCoyoteBid && bid.shipperOpsGroupOffice
        ? bid.shipperOpsGroupOffice.label
        : null,
    totalRounds:
      typeof bid.totalRounds === "object"
        ? bid.totalRounds?.value
        : parseInt(bid.totalRounds),
    bidType: bid.bidType?.value ?? null,
    opportunityId: isCoyoteBid ? bid.opportunityId : null,
    salesforceOpportunityId: bid.salesforceOpportunityId,
    previousSalesforceOpportunityId: !isCoyoteBid
      ? bid.previousSalesforceOpportunityId
      : null,
    locationCode: !isCoyoteBid 
      ? processLocationCode(bid.locationCode)
      : null,
    operationalCenters: !isCoyoteBid && bid.operationalCenters
      ? Array.isArray(bid.operationalCenters)
        ? bid.operationalCenters.map(c => c.value || c)
        : bid.operationalCenters
      : null,
    freightOptimizerCodes: processedFoCodes,

    // Financials
    marginExpectationPercentage: parseFloat(bid.marginExpectationPercentage),
    annualSpend: bid.annualSpend ? parseFloat(bid.annualSpend) : null,
    maxCargoValue: bid.maxCargoValue ? parseFloat(bid.maxCargoValue) : null,

    // Timeline
    openDateUTC: bid.openDateUTC,
    closeDateUTC: bid.closeDateUTC,
    additionalBidRounds: bid.additionalBidRounds
      ? (arr => arr.length ? arr.map(r => ({ roundNumber: r.roundNumber, dueDateUTC: r.dueDateUTC })) : null)(
          bid.additionalBidRounds.filter(r => r?.dueDateUTC)
        )
      : null,
    startDateUTC: bid.startDateUTC,
    endDateUTC: bid.endDateUTC,
    awardDateUTC: bid.awardDateUTC ? bid.awardDateUTC : null,
    strategicStartDateUTC: bid.strategicStartDateUTC
      ? bid.strategicStartDateUTC
      : null,
    strategicEndDateUTC: bid.strategicEndDateUTC
      ? bid.strategicEndDateUTC
      : null,

    // Mode Information & Bid Stratergy
    modes: bid.modes.map(m => {
      if (m.mode === modeType.tl) {
        return mapTlDetails(m, isCoyoteBid);
      } else if (m.mode === modeType.ltl) {
        return mapLtlDetails(m);
      }
      return mapImdlDetails(m, isCoyoteBid, crmEnableImdlUi);
    }),
    isPricedBySales: isPricedBySales
  };
  return mappedBidDetails;
};

const mapBidModeStrategy = mode => ({
  bidStrategy: mode.bidStrategy.value,
  shipperTenderStrategy: mode.shipperTenderStrategy.value,
  expectedLanesPerDay: parseInt(mode.expectedLanesPerDay),
  goalLanesPerDay: parseInt(mode.goalLanesPerDay),
  acceptanceParameters: mode.acceptanceParameters,
  notes: mode.strategyNotes
});

const mapTlDetails = (mode, isCoyoteBid) => ({
  mode: mode.mode,
  laneCount: parseInt(mode.laneCount),
  volume: parseInt(mode.volume),
  fuelSurchargeType: mode.fuelSurchargeType?.value ?? 0,
  fuelSurchargePeg: isCoyoteBid
    ? isNaN(parseFloat(mode.fuelSurchargePeg))
      ? 0
      : parseFloat(mode.fuelSurchargePeg)
    : null,
  equipmentTypes: mode.equipmentTypes.map(e => e.type),
  currencyType: mode.currencyType.value,
  notes: mode.notes,
  equipmentLengths: mode.equipmentTypes
    .filter(e => e.length !== null)
    .map(e => e.length),
  strategy: {
    ...mapBidModeStrategy(mode),
    strategicQuantile: mode.strategicQuantile ? mode.strategicQuantile : 50
  },
  internationalLanes: mode.internationalLanes
    ? mode.internationalLanes.map(i => i.value)
    : null,
  additionalCurrencies: mode.additionalCurrencies?.map(i => i.value) || null,
  rxoFuel: !isCoyoteBid ? parseFloat(mode.rxoFuel) : null,
  customerFuel: !isCoyoteBid ? parseFloat(mode.customerFuel) : null,
  customerBranchAndNetworkTimeframeStartDate:
    !isCoyoteBid && mode.customerBranchAndNetworkTimeframeStartDate
      ? mode.customerBranchAndNetworkTimeframeStartDate
      : null,
  customerBranchAndNetworkTimeframeEndDate:
    !isCoyoteBid && mode.customerBranchAndNetworkTimeframeEndDate
      ? mode.customerBranchAndNetworkTimeframeEndDate
      : null,
  bidSubmissionType: mode.bidSubmissionType ?? 0,
  bidSubmissionDetails: mode.bidSubmissionDetails ?? "",
  bidLoginDetails: mode.bidLoginDetails ?? ""
});

const mapLtlDetails = mode => ({
  mode: mode.mode,
  laneCount: parseInt(mode.laneCount),
  volume: parseInt(mode.volume),
  lTLFreightClasses: mode.ltlFreightClasses.map(m => m.value),
  palletsStackable: mode.palletsStackable.value,
  ltlIncumbentCarriers: mode.ltlIncumbentCarriers,
  ltlPreferredCarriers: mode.ltlPreferredCarriers,
  ltlRateReturnFormat: mode.ltlRateReturnFormat.value,
  requiresGuaranteedShipment: mode.requiresGuaranteedShipment.value,
  ltlAccessorials: mode.ltlAccessorials.map(a => a.value),
  notes: mode.notes,
  additionalCurrencies: mode.additionalCurrencies?.map(i => i.value) || null,
  strategy: mapBidModeStrategy(mode)
});

const mapImdlDetails = (mode, isCoyoteBid, crmEnableImdlUi = false) => {
  const imdlDetails = {
    mode: mode.mode,
    laneCount: parseInt(mode.laneCount),
    volume: parseInt(mode.volume),
    fuelSurchargeType: mode.fuelSurchargeType?.value ?? null,
    fuelSurchargePeg: mode.fuelSurchargePeg ? parseFloat(mode.fuelSurchargePeg) : null,
    equipmentLengths: mode.equipmentLengths?.map(e => e.value) ?? null,
    palletCount: mode.palletCount ? parseInt(mode.palletCount) : null,
    competitors: mode.competitors ? mode.competitors : null,
    notes: mode.notes ? mode.notes : null,
    additionalCurrencies: mode.additionalCurrencies?.map(i => i.value) || null,
    strategy: mapBidModeStrategy(mode),
    rxoFuel: !isCoyoteBid ? parseFloat(mode.rxoFuel) : null,
    customerFuel: !isCoyoteBid ? parseFloat(mode.customerFuel) : null
  };

  if (crmEnableImdlUi) {
    return {
      ...imdlDetails,
      currencyType: mode.currencyType.value,
      bidSubmissionType: mode.bidSubmissionType ?? 0,
      bidLoginDetails: mode.bidLoginDetails ?? ""
    };
  }

  return imdlDetails;
};

export default mapBidDetails;
